import { Box, Flex, Paginator, Table } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { type FC, useCallback, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks';

import { NoTransactions } from '../NoTransactions/NoTransactions';
import { getColumns } from './TransactionsList.config';
import { itemsPerPage } from './TransactionsList.constants';
import { useTransactionMenu, useTransactions, useTransactionsSort } from './TransactionsList.hooks';
import { type TransactionsListProps } from './TransactionsList.types';

export const TransactionsList: FC<TransactionsListProps> = (props) => {
    const { filter } = props;

    const company = useCompanyBasedOnUrl();

    const [pageQuery, setPageQuery] = hooks.useQueryParam('page');

    const menuItems = useTransactionMenu();

    const { transactionsSort, handleSortChange } = useTransactionsSort();

    const currentPage = Number(pageQuery || '0');

    const { transactions, isLoading, totalItems, hasMore } = useTransactions({
        companyId: company.id,
        page: currentPage,
        filter,
        sort: transactionsSort,
    });

    const columns = useMemo(() => getColumns(transactionsSort), [transactionsSort]);

    const handlePageChange = useCallback(
        (page: number) => {
            setPageQuery(String(page));
        },
        [setPageQuery]
    );

    const showPaginator = !isLoading && totalItems > itemsPerPage;

    if (!transactions.length && !isLoading) {
        return <NoTransactions />;
    }

    return (
        <Box color='white100' radius='small' spacing='16 32 16 24'>
            <Flex direction='column' alignItems='center' spacing='24'>
                <Table
                    columns={columns}
                    data={transactions}
                    headerColor='white100'
                    rowActionMenuItems={menuItems}
                    progress={isLoading}
                    onSort={handleSortChange}
                />

                {showPaginator && (
                    <Paginator currentPageIndex={currentPage} isLastPage={!hasMore} onPageChange={handlePageChange} />
                )}
            </Flex>
        </Box>
    );
};

TransactionsList.displayName = 'TransactionsList';
